import { ArrayUtil, Model, PermissionsUtil, StringUtil, UserSettings } from "@mcleod/core";
import {
    Component, ComponentSource, DataSourceAction, DataSourceExecutionEvent, DataSourceMode, DialogProps, LabelProps,
    Layout, LayoutProvidedCrudDecoratorProps, ScreenStack, Tab, Tabset
} from "@mcleod/components";
import { CommonTooltips } from "../CommonTooltips";
import { CommonDialogs } from "../CommonDialogs";
import { KeyValuePair } from "../KeyValuePair";
import { UserList } from "./UserList";
import { UserLayoutProvider } from "../UserLayoutProvider";

export class UserUtil {
    public static readonly SLIDEOUT_MAX_WIDTH = "80%";

    public static getCrudDecoratorProps(): LayoutProvidedCrudDecoratorProps {
        return {
            addlAccessCallback: (key: string, _mode: DataSourceMode) => {
                if (key !== UserSettings.getUserId() && !this.userHasUserListAccess()) {
                    return "Access denied due to permissions";
                }
            }
        };
    }

    public static userHasUserListAccess(): boolean {
        return !PermissionsUtil.isUserDeniedLayout("common/user/UserList")
    }

    public static setAdminComponentVisibility(component: Component) {
        component.visible = this.userHasUserListAccess();
    }

    public static handleScrollToTab(layout: Layout, tabset: Tabset) {
        layout.addLayoutDataLoadListener(() => {
            const targetTabId = window.history.state?.state?.scrollTo;
            if (!StringUtil.isEmptyString(targetTabId)) {
                tabset.components.forEach((tab: Component) => {
                    if (tab.id === targetTabId) {
                        (tab as Tab).selectAndScroll();
                    }
                });
            }
            window.history.replaceState({...window.history.state, scrollTo: null}, null);
        });
    }

    public static triggerSettingsRefresh(userBeingUpdated: string, layout: Layout, event: DataSourceExecutionEvent) {
        if (event.getAction() === DataSourceAction.UPDATE) {
            if (userBeingUpdated === UserSettings.getUserId()) {
                const screenInOverlay = ScreenStack.getNewestOverlay()?.component.isOrContains(layout);
                if (!screenInOverlay) {
                    UserLayoutProvider.refreshSettings(layout);
                } else {
                    UserList.refreshOnCrudClose = true;
                }
            }
        }
    }

    public static async displayGroupMembership(userId: string, userName: string) {
        const keyValuePairs = await this.discoverUserGroups(userId);
        let dialogContent: ComponentSource;
        if (!ArrayUtil.isEmptyArray(keyValuePairs)) {
            const headerLabelText = `${userName} is a member of the following groups:`
            dialogContent = CommonTooltips.createKeyValueTooltipContent(keyValuePairs, headerLabelText, null, true);
            dialogContent.minHeight = 75;
            dialogContent.minWidth = 400;
        } else {
            const noResultslabelProps: Partial<LabelProps> = {
                text: `${userName} is not a member of any user groups.`
            };
            dialogContent = noResultslabelProps;
        }
        const dialogProps: Partial<DialogProps> = { title: "Group Membership" };
        CommonDialogs.showDialog(dialogContent, dialogProps);
    }

    private static async discoverUserGroups(userId: string): Promise<KeyValuePair[]> {
        const result: KeyValuePair[] = [];
        const response = await Model.search("common/user/view-group-membership", { user_id: userId });
        return response?.modelRows?.map(userGroupInfo => {
            const groupId = userGroupInfo.get("group_id");
            const groupDescr = userGroupInfo.get("group_descr");
            return new KeyValuePair(groupId, groupDescr);
        }) ?? [];
    }
}
